import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Store, Info } from "lucide-react";

export default function ReceivePaymentDialog({ open, onOpenChange, customer, balance, onDone }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [saleId, setSaleId] = useState("__auto__"); // Select requires non-empty value
  const [shopId, setShopId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [shops, setShops] = useState([]);
  const receivable = balance?.receivable || 0;
  const pending = balance?.credit_sales_pending || [];

  useEffect(() => {
    if (open) {
      setAmount(receivable > 0 ? String(receivable) : "");
      setMode("cash");
      setSaleId("__auto__");
      setShopId("");
      setNotes("");
      // Load shops
      api.get("/shops").then(({ data }) => {
        setShops(data.items || []);
        if (data.items?.length === 1) {
          setShopId(data.items[0].id);
        }
      }).catch(() => setShops([]));
    }
  }, [open, receivable]);

  // When a specific sale is selected, auto-select its shop
  useEffect(() => {
    if (saleId && saleId !== "__auto__") {
      const sale = pending.find(s => s.sale_id === saleId);
      if (sale?.shop_id) {
        setShopId(sale.shop_id);
      }
    }
  }, [saleId, pending]);

  // Calculate excess amount (payment - receivable)
  const amt = Number(amount) || 0;
  const excessAmount = Math.max(0, amt - receivable);
  const hasExcess = excessAmount > 0.01;

  const submit = async () => {
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    // No longer blocking excess payments - they will be stored as advance
    setBusy(true);
    try {
      await api.post(`/credit/${customer.id}/payment`, {
        amount: amt,
        payment_mode: mode,
        sale_id: saleId === "__auto__" ? null : saleId,
        shop_id: shopId || null,
        notes,
      });
      if (hasExcess) {
        toast.success(`Payment recorded: ₹${Math.min(amt, receivable).toFixed(2)} applied to invoices, ₹${excessAmount.toFixed(2)} stored as advance`);
      } else {
        toast.success(`Payment of ₹${amt.toFixed(2)} recorded`);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="receive-payment-dialog">
        <DialogHeader>
          <DialogTitle>Receive Payment</DialogTitle>
          <DialogDescription>
            From <span className="font-medium">{customer?.name}</span> · Outstanding{" "}
            <span className="font-mono-data text-orange-700">₹{receivable.toFixed(2)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Amount *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              placeholder="0.00"
              data-testid="payment-amount-input"
            />
            {hasExcess && (
              <div className="mt-2 p-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-xs flex items-start gap-2" data-testid="excess-payment-info">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">Payment exceeds outstanding (₹{receivable.toFixed(2)})</div>
                  <div className="text-blue-700 mt-0.5">
                    ₹{Math.min(amt, receivable).toFixed(2)} will clear invoices, 
                    ₹{excessAmount.toFixed(2)} will be stored as <span className="font-semibold">advance/store credit</span> for future use
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Payment Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1" data-testid="payment-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Apply To</Label>
            <Select value={saleId} onValueChange={setSaleId}>
              <SelectTrigger className="mt-1" data-testid="payment-sale-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto (oldest unpaid first — FIFO)</SelectItem>
                {pending.map((s) => (
                  <SelectItem key={s.sale_id} value={s.sale_id}>
                    {formatDate(s.created_at)} · {s.shop_name} · ₹{s.balance.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">
              <Store className="w-3 h-3" /> Shop Receiving Payment
            </Label>
            <Select value={shopId || "__auto_shop__"} onValueChange={(v) => setShopId(v === "__auto_shop__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="payment-shop-select">
                <SelectValue placeholder="Auto (from sale shop)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto_shop__">Auto (from sale shop)</SelectItem>
                {shops.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Payment will reflect in this shop&apos;s financials
            </p>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" rows={2} placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !amount} style={{ backgroundColor: "#2D6A4F" }} className="text-white" data-testid="payment-submit">
            {busy ? "Saving…" : "Receive Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
